import { Link } from 'react-router';

import {
  IconCornerDownRight,
  IconEye,
  IconEyeOff,
  IconGripVertical,
  IconTrash,
} from '@tabler/icons-react';
import { VideoSearchResult } from 'icons';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Draggable } from 'react-smooth-dnd';

import { IconButton, Tag } from '@cfa/react-components';

import Constants from 'constants/index';
import { itemByLanguage } from 'utils/language';
import { getIconFromReference } from 'utils/reference';

import { DocumentDto } from '@/_generated/api-clients/xp';
import { userPrefsAtom } from '@/atoms/userPrefs';

interface AdminDocumentProps {
  disableSixDotMenuHandles?: boolean;
  document: DocumentDto;
  documentIsCurrentlyVisible?: boolean;
  handleDocumentVisibilityToggle?: () => void;
  handleDocumentDeleteButton?: () => void;
  inSectionPreview?: boolean;
  openInNewTab: boolean;
}
const Document: React.FC<AdminDocumentProps> = ({
  disableSixDotMenuHandles,
  document,
  documentIsCurrentlyVisible,
  handleDocumentVisibilityToggle,
  handleDocumentDeleteButton,
  inSectionPreview,
  openInNewTab,
}) => {
  const {
    name: docName,
    id: docId,
    references: ref,
    type: documentType,
  } = document;
  const { t } = useTranslation();
  const userPrefs = useAtomValue(userPrefsAtom);
  const documentId = docId ?? itemByLanguage(ref)?.documentId;
  const docIcon = ref
    ? getIconFromReference(ref)
    : document.icon === 'placeholder'
      ? Constants.DEFAULT_PLACEHOLDER_ICON
      : document.icon;

  const localDocName = itemByLanguage(docName, userPrefs.country.id ?? '');

  const enDocName = itemByLanguage(docName, Constants.LANGUAGE_OPTIONS.ENGLISH);

  const esDocName = itemByLanguage(docName, Constants.LANGUAGE_OPTIONS.SPANISH);

  return (
    <>
      {/** @ts-expect-error it's yelling about children but we need children for the drag and drop */}
      <Draggable key={`document-${docId}`}>
        <div className="flex w-full items-center justify-between">
          {!inSectionPreview ? (
            <Link
              data-testid={documentId}
              key={documentId}
              rel={openInNewTab ? 'noopener noreferrer' : ''}
              target={openInNewTab ? '_blank' : '_self'}
              to={`/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}/${documentId}`}
            >
              <div className="my-4 flex items-center leading-5">
                {documentType?.toLowerCase() ===
                Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE ? (
                  <VideoSearchResult className="mr-2.5!" />
                ) : (
                  <img
                    className="mr-2.5 size-10 rounded-none p-px"
                    alt=""
                    src={`${Constants.PATHWAY_CDN_IMG.PROCEDURES}${
                      docIcon ?? Constants.DEFAULT_PLACEHOLDER_ICON
                    }.svg`}
                  />
                )}
                {localDocName}
              </div>
            </Link>
          ) : (
            <>
              {!disableSixDotMenuHandles && (
                <IconGripVertical className="document-drag-handle text-grayscale-gray3! absolute! left-0! ml-8! size-6! cursor-move!" />
              )}
              <div className="font-base mx-15 my-4 flex w-full items-center leading-5">
                {documentType?.toLowerCase() ===
                Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE ? (
                  <VideoSearchResult className="mr-2.5!" />
                ) : (
                  <img
                    className="mr-2.5 size-10 rounded-none p-px"
                    alt=""
                    src={`${Constants.PATHWAY_CDN_IMG.PROCEDURES}${
                      docIcon ?? Constants.DEFAULT_PLACEHOLDER_ICON
                    }.svg`}
                  />
                )}
                {enDocName}
              </div>
              <button
                type="button"
                className="mr-4.5 cursor-pointer"
                onClick={() => handleDocumentVisibilityToggle?.()}
              >
                {documentIsCurrentlyVisible ? (
                  <Tag
                    className="ml-4! h-fit! flex-row-reverse! items-center! [&_.tag-leading-element]:mr-1.5! [&_.tag-leading-element]:size-auto!"
                    data-testid="EditDocumentToggleShow"
                    label={t('Admin.categoryIsVisible')}
                    leadingElement={<IconEye width={10} />}
                    variant="filled"
                  />
                ) : (
                  <Tag
                    className="h-fit! flex-row-reverse! items-center! [&_.tag-leading-element]:mr-1.5! [&_.tag-leading-element]:size-auto!"
                    data-testid="EditDocumentToggleHide"
                    disabled
                    label={t('Admin.categoryHidden')}
                    leadingElement={<IconEyeOff width={10} />}
                    variant="filled"
                  />
                )}
              </button>
              <IconButton
                className="mr-9! ml-0!"
                aria-label="Delete Category"
                color="default"
                data-testid="EditCategoryTrashIcon"
                href=""
                onClick={() => handleDocumentDeleteButton?.()}
                size="md"
              >
                <IconTrash />
              </IconButton>
            </>
          )}
        </div>
        {!inSectionPreview ? null : (
          <>
            <div className="ml-[115px]! p-0!">
              {t('Admin.spanishTranslation')}
            </div>
            <div className="font-base mx-[75px] my-2 line-clamp-2 flex items-center text-base/5">
              <IconCornerDownRight size={35} />
              {documentType?.toLowerCase() ===
              Constants.EXPANDED_FILE_FORMAT_TYPES.BRIGHTCOVE ? (
                <VideoSearchResult className="mr-2.5!" />
              ) : (
                <img
                  className="mr-2.5 size-10 rounded-none p-px"
                  alt=""
                  src={`${Constants.PATHWAY_CDN_IMG.PROCEDURES}${
                    docIcon ?? Constants.DEFAULT_PLACEHOLDER_ICON
                  }.svg`}
                />
              )}
              {esDocName}
            </div>
          </>
        )}
      </Draggable>
    </>
  );
};

export default Document;
